import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router';
import { UserContextV2 } from '~/app';
import { isNotificationArrival } from '~/features/notifications/constants';
import { createForcedDocumentsRefresher } from '~/features/vault/documents/forcedDocumentsRefresh';
import { forceVaultPageRefresh } from '~/features/vault/forceVaultPageRefresh';
import { PROVIDER_PAGE_KEYS, vaultPageFromPathname } from './routes';

/**
 * [MCV-1753] Bridges react-router client-side navigation to the vault machine. The vault
 * counterpart of `useMarketPageSync` ([MCV-1537]).
 *
 * Each `/vault/*` page loads its data in that page-state's `entry` action in
 * `features/vault/account/vaultState.machine.ts`, and those states are reachable ONLY via
 * `ON_SET_PAGE`. The production senders were the expanded sidebar's click, the brand menu's "Go to
 * dashboard", and the boot self-send. Everything else that merely changes the URL (notification
 * rows, dashboard cards, alert chips, Next Steps, the collapsed-rail flyout, browser back/forward,
 * `<Navigate>` redirects) never told the machine, so the destination page rendered a section actor
 * that was never asked to load: an empty grid until a refresh re-ran the boot self-send.
 *
 * Two guards, both load-bearing (the useVaultPageSync deleted in #1026 got both wrong):
 *
 *  - Guard 1: never send while `$_LOAD_PROVIDER` is in flight. `ON_SET_PAGE` exits that state and
 *    CANCELS `waitForProvider`, so `provider` is never assigned and the vault spins forever. This
 *    needs no handling of its own: the boot self-send reads the live URL when the load lands.
 *  - Guard 2: skip when `currentPage` already equals the page. The expanded sidebar sends
 *    `ON_SET_PAGE` before it navigates, so without this a sidebar click would fetch twice.
 *
 * It never fires on mount, for the same reason (boot owns the first load). That is safe because the
 * Alerts panel only exists inside the vault layout, so every notification click happens after this
 * hook has mounted.
 *
 * A notification row's link carries `state.fromNotification`. That forces a refetch even when the
 * click lands on the page already on screen (Guard 2 is bypassed, and a same-path click is still a
 * new `location.key`), which is what a delegate's edit needs to become visible. Hash- and
 * search-only replaces (licence tabs, settings tabs) are ignored unless they are forced. Only the
 * click forces: the history entry keeps its `state`, so going back or forward onto it (a POP) is a
 * plain navigation (`isNotificationArrival`).
 *
 * Three forced cases refresh in place instead of re-entering the page state (see the branches
 * below): documents, whose service ignores a refresh while it is busy; the profile form while it is
 * already on screen, whose entry would reset what the user is typing; and the dashboard while it is
 * already on screen, which refreshes itself and sends nothing here.
 *
 * `/vault/faq` is under the vault header but has no machine branch, so it is excluded: it would
 * otherwise fall through to `$_DASHBOARD_VIEW` and fetch certifications.
 *
 * React runs a child route's `<Navigate>` effect before this one, so a redirected-away-from path
 * still reaches the effect. `settings` is the one redirect that matters (a delegating employer is
 * bounced to the dashboard, and the settings entry would load employer-association data for it), so
 * it is skipped while delegating, the same check the vault side menu uses to hide that link.
 */
export const useVaultPageSync = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const vaultRef = UserContextV2.useSelector((state) => state.context.vaultRef);
  const isDelegating = UserContextV2.useSelector((state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_IMPERSONATING' })
  );
  const prevKeyRef = useRef(location.key);
  const prevPathRef = useRef(location.pathname);
  // Owns the one deferred documents refresh, if there is one waiting on a busy service.
  const [documentsRefresher] = useState(createForcedDocumentsRefresher);

  // The deferred refresh outlives the effect below (it re-runs on every navigation) but not the
  // vault it was queued against, nor the layout.
  useEffect(() => () => documentsRefresher.cancel(), [vaultRef, documentsRefresher]);

  useEffect(() => {
    // Never fire on mount, and never twice for one navigation: boot is owned by the machine's own
    // self-send, and `location.key` changes on every navigation, same-path ones included.
    if (location.key === prevKeyRef.current) return;
    prevKeyRef.current = location.key;

    const pathChanged = location.pathname !== prevPathRef.current;
    prevPathRef.current = location.pathname;
    const forced = isNotificationArrival(location.state, navigationType);
    // Hash- or search-only replaces (licence tabs, settings tabs) are not page changes.
    if (!pathChanged && !forced) return;

    if (!vaultRef) return;
    const page = vaultPageFromPathname(location.pathname);
    if (!page) return;
    if (isDelegating && page === PROVIDER_PAGE_KEYS.settings) return;

    const snap = vaultRef.getSnapshot();
    if (snap.matches('$_LOAD_PROVIDER')) return;

    // Forced (a notification click): the page is refreshed even when it is the one on screen.
    // `forceVaultPageRefresh` holds the three in-place refreshes (documents, profile form,
    // dashboard) and the re-entry for everything else, shared with the live websocket path.
    if (forced) {
      forceVaultPageRefresh(vaultRef, page, { hash: location.hash, documentsRefresher });
      return;
    }

    if (snap.context.currentPage === page) return;

    // The hash names the tab the destination shows (academics reloads per tab).
    vaultRef.send({ type: 'ON_SET_PAGE', payload: { page, hash: location.hash } });
  }, [
    location.key,
    location.pathname,
    location.hash,
    location.state,
    navigationType,
    vaultRef,
    isDelegating,
    documentsRefresher,
  ]);
};
